/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.19.0
 */

import { memo, ReactNode, useEffect, useState } from 'react';

import { icons } from '../../../assets';
import { ImgStyled } from '../../../components/common/Block/Img';
import EllipsisText from '../../../components/common/EllipsisText/EllipsisText';
import { useTableFilterApi } from '../api';
import { ETableSort, ITableSort } from '../types';
import { useTableSort } from './Filter/hooks/useTableSort';
import { TableHeaderStyled } from './styled/TableHeaderStyled';

interface ITableHeader {
    label: string;
    name: string;
    description?: string;
    disableSort?: boolean;
    children?: ReactNode;
}

const TableHeader = ({ children, label, description, disableSort, name }: ITableHeader) => {

    const [currentSort, setCurrentSort] = useState<ITableSort | undefined>(undefined);

    const { sort } = useTableSort(name);

    
    /* API */
    const apiFilter = useTableFilterApi();

    const onSortClick = () => {
        if (disableSort) return;

        const newSort = {
            property: name,
            direction: currentSort?.direction
                ? ({
                    [ETableSort.asc]: ETableSort.desc,
                    [ETableSort.desc]: ETableSort.default,
                    [ETableSort.default]: ETableSort.asc,
                }[currentSort.direction])
                : ETableSort.asc,
        };
        setCurrentSort(newSort);

        apiFilter.order(newSort);
    };

    useEffect(() => {
        setCurrentSort(sort);
    }, [sort]);

    return (
        <TableHeaderStyled
            isSortable={!disableSort}
            isSortActive={currentSort?.direction === ETableSort.asc || currentSort?.direction === ETableSort.desc}
        >
            <div
                className="header-cell__label"
                onClick={onSortClick}
            >
                {!!label && <EllipsisText title={label} tooltipMsg={description}>{label}</EllipsisText>}
                {!disableSort && (
                    <ImgStyled
                        className="small"
                        src={(currentSort?.direction === ETableSort.asc)
                            ? icons.arrowSortAsc
                            : (currentSort?.direction === ETableSort.desc)
                                ? icons.arrowSortDesc
                                : icons.arrowSortDefault}
                    />
                )}
            </div>
            {children}
        </TableHeaderStyled>
    );
};

export default memo(TableHeader);
